import { useDataTable } from "components/data-table/hooks/useDataTable";

import type {
  DataTableRow,
  DataTableRowSchema,
} from "components/data-table/types";
import DecoratedNumber from "components/decorated-number";
import AssetCell from "components/table/advanced/cells/asset";
import DisplayCell from "components/table/advanced/cells/display";
import * as fns from "date-fns";
import { defineField } from "@/form/useForm";
import { useForexTickers } from "@/integrations/findex/hooks/forex/useForexTickers";
import { AssetImageV2 } from "components/asset-image-v2";
import { useUserSettings } from "@/modules/settings/hooks/useUserSettings";
import { getPrettyAssetUnit, prettyAssetType } from "@/utils/asset";
import { numberToHumanReadable } from "@/utils/formatting/number";
import {
  getInvestmentCurrentValue,
  getInvestmentCurrentValueCurrency,
  getInvestmentInitialCurrency,
  getInvestmentInitialValue,
} from "@/utils/investment";
import { computeRoi } from "@/utils/roi";
import { clip } from "@/utils/string";
import {
  EAssetSubtype,
  EAssetType,
  getRefId,
  type FullAsset,
  type FullInvestment,
  type PotentialBankAccount,
} from "findef";
import {
  computed,
  defineComponent,
  watchEffect,
  type CSSProperties,
} from "vue";
import Tooltip from "components/tooltip";
import TinyBadge from "components/tiny-badge";
import Switchinput from "components/form/switch-input";
import { useDataTableState } from "components/data-table/hooks/useDataTableState";
import { ref } from "vue";
import { DataTable } from "components/data-table";
import Centered from "components/centered";
import Section from "components/section";
import ProviderCard from "@/modules/asset/components/provider-card";
import InfoCard from "@/modules/asset/components/info-card";
import EmptyState from "components/empty-state";
import LinkIcon from "assets/img/svg/icons/chain-link.svg?url";
import Button from "components/button";
import { parseDate } from "@/utils/formatting/date";
import InfoIcon from "assets/img/svg/icons/info-circle.svg?url";
import Icon from "components/icon";
import { prettyNumber } from "@/utils/number/format";
import { toTitle } from "@/utils/string";
import { watch } from "vue";
import { useFormField } from "@/form/useForm/useFormField";
import { clamp, fract, sum, unique, uniqueBy } from "math";
import type { IHeaderProps } from "components/header/types";
import { useElementBounding } from "@vueuse/core";
import { unwrapElementRef } from "@/utils/dom/unwrap";
import { getTextMetrics } from "@/utils/html";
import type { UseProviderImport } from "../hooks/useProviderImport";
import { getExternalIdV2, matchesExternalIdV2 } from "../utils/externalIdV2";
import { sendFeaturebaseFeedback } from "@/integrations/featurebase/utils";
import { useResponsive } from "~/composables/useResponsive";

const safeNumber = (x: number): number => (isNaN(x) || !isFinite(x) ? 0 : x);

const SpecialInput = defineComponent<{
  value: number;
  onValueChange: (value: number) => any;
}>(
  (props) => {
    const valString = computed(() => props.value + "%");
    const valueLen = computed(() => valString.value.length);
    const inputRef = ref();
    const inputBound = useElementBounding(inputRef);
    const numFractions = computed(
      () => (fract(Number(props.value)) + "").length,
    );

    const itemStyle = computed((): CSSProperties => {
      const inputEl = unwrapElementRef(inputRef);
      if (!inputEl) return {};
      const inp = inputEl as HTMLInputElement;
      const inpStyle = getComputedStyle(inp);
      const m = getTextMetrics(valString.value, inpStyle.font);
      const xoff = Math.max(
        (valueLen.value - 2) * ((m?.width ?? 0) * 0.5),
        Math.max(m?.width ?? 0, 16),
      );
      const x = inputBound.x.value + xoff;
      const y =
        inputBound.y.value +
        0.5 * inputBound.height.value -
        0.5 * (m?.fontBoundingBoxAscent ?? 0) +
        0.5 * (m?.ideographicBaseline ?? 0);
      return {
        position: "fixed",
        left: x + "px",
        top: y + "px",
        ...(valueLen.value > 4 ||
          Number(props.value) > 100 ||
          numFractions.value > 3
          ? {
            display: "none",
            opacity: "0%",
          }
          : {}),
      };
    });

    return () => {
      return (
        <span class="relative overflow-hidden">
          <input
            ref={inputRef}
            type="number"
            min="0"
            max="100"
            step="any"
            onInput={(ev) => {
              const target = ev.target as HTMLInputElement;
              props.onValueChange(safeNumber(Number(target.value)));
              inputBound.update();
            }}
            value={props.value}
            class="bg-gray-50 border border-gray-300 flex-col justify-center items-start inline-flex focus:ring-transparent text-xs text-gray-900 px-2 font-normal w-full h-full"
          />
          <span class="text-gray-900 text-xs" style={itemStyle.value}>
            %
          </span>
        </span>
      );
    };
  },
  { props: ["value", "onValueChange"] },
);

const BankAccount = defineComponent<{
  account: PotentialBankAccount;
  hook: UseProviderImport;
}>(
  (props) => {
    const settings = useUserSettings();
    const forex = useForexTickers();
    const tableState = useDataTableState({});
    const responsive = useResponsive();

    const investments = computed(() => {
      const data = props.account.investments;
      return Array.isArray(data) ? data : [];
    });
    const liabilities = computed(() => {
      const data = props.account.liabilities;
      return Array.isArray(data) ? data : [];
    });

    const isLoanAccount = computed(() => {
      return (props.account.liabilities || []).length > 0;
    });

    const totalBorrowedAmount = computed(() => {
      return sum(
        liabilities.value.map(
          (it) =>
            Math.abs(it.amount?.value ?? 0) *
            forex.getTickerRef(
              it.amount?.type || settings.currency.value,
              settings.currency.value,
            ).value.bid,
        ),
      );
    });

    const selectedPositionIds = computed(
      () => props.hook.providerImport.value.selected.investmentIds || [],
    );
    const selectedLiabilityPositionIds = computed(
      () => props.hook.providerImport.value.selected.liabilityIds || [],
    );

    const theseSelectedPositionIds = computed(() =>
      selectedPositionIds.value.filter((posId) =>
        investments.value.some(inv => matchesExternalIdV2(posId, inv))
      )
    );
    const theseSelectedLiabilityPositionIds = computed(() =>
      selectedLiabilityPositionIds.value.filter((posId) =>
        liabilities.value.some(liab => matchesExternalIdV2(posId, liab))
      )
    );

    const nonLoanInvestments = computed(() =>
      investments.value.filter(
        (inv) => !inv?.asset?.subtypes?.includes(EAssetSubtype.LOAN),
      ),
    );

    const totalValue = computed(() => {
      return (
        nonLoanInvestments.value.reduce((acc, inv) => {
          const currentValue = getInvestmentCurrentValue(inv as FullInvestment);
          const currentValueCurrency = getInvestmentCurrentValueCurrency(
            inv as FullInvestment,
          );
          const tickerCurrent = forex.getTickerRef(
            currentValueCurrency || "SEK",
            settings.currency.value,
          );
          return acc + currentValue * tickerCurrent.value.bid;
        }, 0) || 0
      );
    });

    const allSelected = ref<boolean>(false);

    const initialAllSelected = computed(() => {
      const liabIds = theseSelectedLiabilityPositionIds.value;
      const liabs = liabilities.value;
      const invs = investments.value;
      const invIds = theseSelectedPositionIds.value;
      if (liabs.length > 0 && liabIds.length >= liabs.length) return true;
      if (invs.length > 0 && invIds.length >= invs.length) return true;
      return false;
    });

    const stopwatch = watch(
      initialAllSelected,
      (all) => {
        allSelected.value = all;
      },
      { immediate: true, deep: true },
    );

    const handleSelect = (investment: { positionId?: string; externalId: string; externalAccountId?: string }) => {
      stopwatch();
      props.hook.toggleInvestment(investment);
      const positionId = getExternalIdV2(investment);
      props.hook.addUserControlledId(positionId);
    };

    const handleSelectLiability = (liability: { positionId?: string; externalId: string; externalAccountId?: string }) => {
      stopwatch();
      props.hook.toggleLiability(liability);
      const positionId = getExternalIdV2(liability);
      props.hook.addUserControlledId(positionId);
    };

    const handleToggleAll = () => {
      stopwatch();
      allSelected.value = !allSelected.value;

      props.hook.setBankAccountInvestmentsSelected(
        props.account.providerAccountId || "",
        allSelected.value,
      );

      if (liabilities.value.length > 0) {
        props.hook.setBankAccountLiabilitiesSelected(
          props.account.providerAccountId || "",
          allSelected.value,
        );
      }
    };

    const investmentRows = computed((): DataTableRow[] => {
      return investments.value.map((inv) => {
        const assetTypeName = prettyAssetType(
          inv.asset.type || EAssetType.UNDEFINED,
          inv.asset.listed,
          inv.asset.subtypes,
          false,
          true,
        );
        const currentValue = getInvestmentCurrentValue(inv as FullInvestment);
        const initialValue = getInvestmentInitialValue(inv as FullInvestment);
        const currentValueCurrency = getInvestmentCurrentValueCurrency(
          inv as FullInvestment,
        );
        const initialValueCurrency = getInvestmentInitialCurrency(
          inv as FullInvestment,
        );
        const tickerCurrent = forex.getTickerRef(
          currentValueCurrency || "SEK",
          settings.currency.value,
        );
        const tickerInitial = forex.getTickerRef(
          initialValueCurrency || "SEK",
          settings.currency.value,
        );
        const profit =
          currentValue * tickerCurrent.value.bid -
          initialValue * tickerInitial.value.bid;
        const text = `${profit > 0 ? "+" : ""}${numberToHumanReadable(profit, 6, settings.currency.value, undefined, false, 0)}`;

        //const azzet = inv.asset;
        //const shouldHaveLogo = azzet ? assetShouldHaveAutomaticLogo(azzet) : false;
        return {
          id: getExternalIdV2(inv),
          columns: [
            {
              key: "asset.name",
              label: "Asset",
              width: 45,
              body: () => (
                <AssetCell
                  class={
                    inv?.asset?.subtypes?.includes(EAssetSubtype.LOAN)
                      ? "opacity-40"
                      : "cursor-pointer hover:opacity-80"
                  }
                  icon={() => (
                    <AssetImageV2
                      asset={inv?.asset as FullAsset}
                      investment={inv as FullInvestment}
                      size="md"
                      variant="gray"
                      iconProps={{ size: "1.5rem" }}
                    />
                  )}
                  title={clip(inv.asset.name, 24)}
                  subtitle={assetTypeName}
                />
              ),
            },
            {
              key: "currentValue",
              label: "Current Value",
              width: 15,
              align: "right",
              body: () => (
                <DisplayCell
                  class={
                    inv?.asset?.subtypes?.includes(EAssetSubtype.LOAN) &&
                    "opacity-40"
                  }
                  title={{
                    text: numberToHumanReadable(
                      (currentValue || 0) * tickerCurrent.value.bid,
                      6,
                      settings.currency.value,
                      [],
                      false,
                      0,
                    ),
                  }}
                  subtitle={
                    !inv.asset.isBankAccount
                      ? {
                        text: numberToHumanReadable(
                          inv.quantity,
                          6,
                          getPrettyAssetUnit(
                            inv.asset.type,
                            inv.quantity === 1 ? false : true,
                          ),
                          [],
                          false,
                        ),
                      }
                      : undefined
                  }
                />
              ),
            },
            {
              key: "ROI",
              label: "ROI",
              width: 15,
              align: "right",
              body: () => (
                <DisplayCell
                  class={
                    inv?.asset?.subtypes?.includes(EAssetSubtype.LOAN) &&
                    "opacity-40"
                  }
                  title={{
                    component: () => {
                      if (!inv?.asset?.subtypes?.includes(EAssetSubtype.LOAN)) {
                        return (
                          <DecoratedNumber
                            iconSize={10}
                            valueClass="text-xs"
                            arrow
                            value={computeRoi(
                              initialValue * tickerInitial.value.bid,
                              currentValue * tickerCurrent.value.bid,
                            )}
                            unit="percent"
                            precision={1}
                            signs={["-", "+"]}
                          />
                        );
                      }
                    },
                  }}
                  subtitle={{
                    text: !inv?.asset?.subtypes?.includes(EAssetSubtype.LOAN)
                      ? text
                      : "",
                  }}
                />
              ),
            },
            {
              label: "  ",
              width: 15,
              body: () =>
                inv.isFirstTimeSeen ? (
                  <TinyBadge size="s" variant="blue">
                    New
                  </TinyBadge>
                ) : (
                  <div />
                ),
            },
            {
              key: "actions",
              label: "Actions",
              width: 10,
              align: "right",
              direction: "rtl",
              body: () => {
                return (
                  <div class="w-full h-full" style={{ direction: "ltr" }}>
                    <Tooltip
                      text="We don't have support for liabilities yet. We'll introduce debt & liabilities in Q4."
                      disabled={
                        !inv?.asset?.subtypes?.includes(EAssetSubtype.LOAN)
                      }
                    >
                      <Switchinput
                        disabled={inv?.asset?.subtypes?.includes(
                          EAssetSubtype.LOAN,
                        )}
                        tooltip={
                          "We don't have support for liabilities yet. We'll introduce debt & liabilities in Q4."
                        }
                        value={!!selectedPositionIds.value.some(posId => matchesExternalIdV2(posId, inv))}
                        onToggle={() => handleSelect(inv)}
                      />
                    </Tooltip>
                  </div>
                );
              },
            },
          ],
        };
      });
    });

    const liabilityRows = computed((): DataTableRow[] => {
      return liabilities.value.map((liab) => {
        return {
          id: liab.externalId || getRefId(liab),
          columns: [
            {
              label: "Liability",
              body: () => {
                return (
                  <div class="grid grid-cols-[max-content_max-content] gap-2.5 cursor-pointer hover:opacity-70">
                    <div class="w-10 h-10 bg-gray-100 justify-center items-center inline-flex text-gray-400">
                      <div class="w-5 h-5 relative overflow-hidden">
                        <Icon
                          src={async () =>
                            import("assets/img/svg/icons/receipt.svg?raw")
                          }
                          fill="currentColor"
                          stroke="none"
                        />
                      </div>
                    </div>
                    <div class="grid grid-rows-2">
                      <div class="text-[#111928] text-xs font-semibold font-['Inter'] leading-[18px]">
                        {liab.name || "---"}
                      </div>
                      <div class="text-gray-500 text-xs font-normal font-['Inter'] leading-[18px]">
                        {`Debt (${liab.subtype ? toTitle(liab.subtype) : ""})`}
                      </div>
                    </div>
                  </div>
                );
              },
            },
            {
              label: "Total Borrowed Amount",
              body: () => {
                const value = liab.amount?.value || 0;
                const currency = liab.amount?.type || settings.currency.value;

                return (
                  <div class="text-[#111928] text-xs font-semibold font-['Inter'] leading-[18px]">
                    {prettyNumber(value, {
                      american: true,
                      suffix: currency,
                      zeroIsInteger: true,
                      maxDecimals: 2,
                    })}
                  </div>
                );
              },
            },
            {
              label: "Interest",
              body: () => {
                return (
                  <div class="grid items-center">
                    <DecoratedNumber
                      value={(liab.interestRate || 0) * -1}
                      unit="percent"
                      valueClass="font-semibold text-xs text-red-500"
                      unitClass="font-semibold text-xs text-red-500"
                    />
                  </div>
                );
              },
            },
            {
              label: "My Loan Share",
              body: () => {
                const shareField = useFormField<number>({
                  schema: defineField<{}, number>({
                    type: "number",
                    value: liab.quantity ?? 1.0,
                  }),
                  transform: (val) => clamp(val, 0.0, 1.0),
                  onChange: (val) => {
                    console.log({ val });
                    props.hook.mutateLiability(liab.externalId, (old) => {
                      return {
                        ...old,
                        quantity: val,
                        userMutatedFields: unique([
                          ...(old.userMutatedFields || []),
                          "quantity",
                        ]),
                      };
                    });
                  },
                });

                return (
                  <div class="w-[100px] h-[30px] grid items-center">
                    <div class="grid grid-cols-[4rem_2rem] w-full h-full">
                      <SpecialInput
                        class="h-[30px] "
                        value={
                          clamp(
                            safeNumber(Number(shareField.dataRef.value.value)),
                            0.0,
                            1.0,
                          ) * 100.0
                        }
                        onValueChange={(val) => {
                          shareField.dataRef.setValue(
                            safeNumber(Number(val)) / 100.0,
                          );
                        }}
                      />
                      <div
                        class="bg-gray-100 h-[30px] border border-gray-300 overflow-hidden grid items-center justify-center"
                        style={{ borderLeft: "none", borderLeftWidth: "0px" }}
                      >
                        <div class="text-[#111928] text-sm font-medium font-['Inter'] leading-[21px]">
                          %
                        </div>
                      </div>
                    </div>
                  </div>
                );
              },
            },
            {
              label: "  ",
              width: 15,
              body: () =>
                liab.isFirstTimeSeen ? (
                  <TinyBadge size="s" variant="blue">
                    New
                  </TinyBadge>
                ) : (
                  <div />
                ),
            },
            {
              key: "actions",
              label: "Include",
              width: 10,
              align: "right",
              direction: "rtl",
              body: () => {
                return (
                  <div class="w-full h-full" style={{ direction: "ltr" }}>
                    <Switchinput
                      value={
                        !!selectedLiabilityPositionIds.value.some(posId => matchesExternalIdV2(posId, liab))
                      }
                      onToggle={() => handleSelectLiability(liab)}
                    />
                  </div>
                );
              },
            },
          ],
        };
      });
    });

    const rows = computed((): DataTableRow[] => {
      if (liabilities.value.length > 0 || liabilityRows.value.length > 0)
        return liabilityRows.value;
      return investmentRows.value;
    });

    watchEffect(() => {
      console.log(liabilities.value);
    });

    const tableSchema = computed((): DataTableRowSchema => {
      if (liabilities.value.length > 0)
        return {
          columns: [],
        };

      return {
        columns: [
          { key: "asset.name", label: "Asset" },
          { key: "currentValue", label: "Current Value" },
          { key: "ROI", label: "ROI" },
          { key: "actions", label: "Actions" },
        ],
      };
    });

    const table = useDataTable({
      loading: ref(false),
      rows: rows,
      state: tableState,
      schema: tableSchema,
    });

    const bannerHeader = computed((): IHeaderProps => {
      if (isLoanAccount.value) {
        return {
          title:
            props.account.name ||
            props.account.providerAccountId ||
            "Bank Account",
          subtitle: responsive.isSmall.value
            ? `${(props.account.liabilities || []).length} liabilities`
            : `${(props.account.liabilities || []).length
            } liabilities with a total borrowed amount of ${numberToHumanReadable(totalBorrowedAmount.value, undefined, settings.currency.value, undefined, false, 2)}`,
        };
      }

      return {
        title:
          props.account.name ||
          props.account.providerAccountId ||
          "Bank Account",
        subtitle: responsive.isSmall.value
          ? `${props.account.investments.length} assets`
          : `${props.account.investments.length
          } assets in total with a total value of ${numberToHumanReadable(totalValue.value, undefined, settings.currency.value, undefined, false, 2)}`,
      };
    });

    return () => {
      return (
        <div>
          <DataTable
            variant="gray"
            rounded
            loading={false}
            disableSorting={true}
            banner={{
              header: bannerHeader,
              actions: [
                {
                  body: () => {
                    return (
                      <div class="grid grid-cols-[auto_auto] gap-1">
                        <Centered class="text-gray-500 text-sm font-medium leading-[14px]">
                          Toggle all
                        </Centered>
                        <Centered>
                          <Switchinput
                            asSwitch
                            value={allSelected.value}
                            onToggle={handleToggleAll}
                          />
                        </Centered>
                      </div>
                    );
                  },
                },
              ],
            }}
            table={table}
            borderlessHeader
          />
        </div>
      );
    };
  },
  { props: ["account", "hook"] },
);

export const SelectPositionsStep = defineComponent<{
  hook: UseProviderImport;
}>(
  (props) => {
    const provider = computed(
      () =>
        props.hook.provider.value || props.hook.providerImport.value.provider,
    );

    const bankAccounts = computed(
      () => props.hook.providerImport.value?.available?.bankAccounts || [],
    );

    const availableInvestments = computed(() =>
      uniqueBy((props.hook.providerImport.value?.available?.bankAccounts || [])
        .map((acc) => acc.investments)
        .flat(), it => getExternalIdV2(it))
    );

    const selectedCount = computed(() => {
      const selectedPositionIds = props.hook.providerImport.value?.selected?.investmentIds || [];
      return selectedPositionIds.filter(posId =>
        availableInvestments.value.some(inv => matchesExternalIdV2(posId, inv))
      ).length || 0
    });

    const lastSync = computed((): Date | undefined => {
      if (!props.hook.providerImport.value) return undefined;
      if (!props.hook.providerImport.value?.lastSync) return undefined;
      return parseDate(props.hook.providerImport.value.lastSync);
    });

    const lastSyncText = computed(() => {
      const date = lastSync.value;
      if (!date) return "Never";
      return fns.format(date, "d MMM, yyyy") || "Never";
    });

    const providerName = computed(
      () => provider.value?.displayName || provider.value?.name || "?",
    );

    const handleConnectNewProvider = () => { props.hook.reset() };

    const connectNewProvider = () => {
      return (
        <Button
          icon={{
            src: LinkIcon,
            stroke: "currentColor",
            size: "0.8rem",
          }}
          onClick={() => handleConnectNewProvider()}
        >
          Try Another Provider
        </Button>
      );
    };

    return () => {
      return (
        <div class="w-full h-full flex flex-col items-center px-4 md:px-8">
          <div class="flex flex-col w-full max-w-[916px] pb-8">
            <Section class="mb-10 grid grid-cols-1 md:grid-cols-2 gap-4">
              {provider.value ? (
                <ProviderCard provider={provider.value} />
              ) : (
                <div>MEGA ERROR</div>
              )}
              <div class="flex flex-row items-center gap-3 md:justify-end">
                <InfoCard
                  subtitleClass="text-sm font-medium"
                  heightClass="h-auto"
                  titleClass="text-gray-500 text-xs font-semibold leading-[21px]"
                  title="Accounts"
                  subtitle={(bankAccounts.value || []).length + ""}
                />
                <InfoCard
                  subtitleClass="text-sm font-medium"
                  heightClass="h-auto"
                  title="Included"
                  titleClass="text-gray-500 text-xs font-semibold leading-[21px]"
                  subtitle={`${selectedCount.value} of ${(availableInvestments.value || []).length}`}
                />
                <InfoCard
                  subtitleClass="text-sm font-medium"
                  heightClass="h-auto"
                  titleClass="text-gray-500 text-xs font-semibold leading-[21px]"
                  title="Last Data Sync"
                  subtitle={`${lastSyncText.value || "Never"}`}
                />
              </div>
            </Section>
            {bankAccounts.value.length === 0 && (
              <div class="w-full flex flex-col items-center flex-1">
                <EmptyState
                  title="No Accounts or Assets Found!"
                  subtitle={`We could not find any accounts or assets for you at ${providerName.value}`}
                  icon={{
                    src: InfoIcon,
                    fill: "currentColor",
                    size: "2rem",
                  }}
                  action={connectNewProvider}
                />
                <div class="text-gray-500 text-sm font-normal font-['Inter'] leading-[21px]">
                  If you have accounts and assets that should be available to
                  you, please&nbsp;
                  <a
                    onClick={() => sendFeaturebaseFeedback()}
                    class="text-primary-500 text-sm font-bold font-['Inter'] underline leading-[21px] cursor-pointer hover:text-primary-600"
                  >
                    Contact Us
                  </a>
                  .
                </div>
              </div>
            )}
            <Section class="space-y-6">
              {bankAccounts.value.length > 0 &&
                bankAccounts.value.map((account) => {
                  return (
                    <BankAccount
                      key={`bank-account-${account.providerAccountId}-${account.name}`}
                      account={account}
                      hook={props.hook}
                    />
                  );
                })}
            </Section>
          </div>
        </div>
      );
    };
  },
  { props: ["hook"] },
);
